import React, { useMemo } from "react";

import { useProjectPageContext } from "./ProjectPageContext";
import ConcretePoursChart from "./metrics/charts/ConcretePoursChart";
import WorkerHoursChart from "./metrics/charts/WorkerHoursChart";
import {
  buildConcretePoursOverviewUi,
  buildWorkerHoursOverviewUi,
} from "./metrics/metricsHelpers";
import ProjectSummaryCard from "./metrics/ProjectSummaryCard";
import ProjectTicketSummaries from "./ProjectTicketSummaries";

/**
 * Render the project overview tab MVP content.
 *
 * @returns {JSX.Element}
 */
const ProjectOverview = () => {
  const {
    placeholderContent,
    workerHoursSummary,
    concretePoursSummary,
    setWorkerHoursRangeKey,
    setWorkerHoursTypeKey,
    setConcretePoursRangeKey,
    setConcretePoursTypeKey,
    overviewMetricsMonthOptions,
  } = useProjectPageContext();
  const { overview } = placeholderContent;

  const workerHoursUi = useMemo(
    () =>
      buildWorkerHoursOverviewUi(
        workerHoursSummary,
        overviewMetricsMonthOptions
      ),
    [workerHoursSummary, overviewMetricsMonthOptions]
  );

  const concretePoursUi = useMemo(
    () =>
      buildConcretePoursOverviewUi(
        concretePoursSummary,
        overviewMetricsMonthOptions
      ),
    [concretePoursSummary, overviewMetricsMonthOptions]
  );

  return (
    <div className="space-y-2">
      <ProjectSummaryCard
        title={overview.workerHours.title}
        subtitle={overview.workerHours.subtitle}
        metrics={workerHoursSummary.metrics}
        error={workerHoursSummary.error}
        typeKey={workerHoursUi.typeKey}
        onTypeChange={setWorkerHoursTypeKey}
        typeOptions={workerHoursUi.typeOptions}
        typeAriaLabel="Worker hours type"
        rangeKey={workerHoursUi.rangeKey}
        onRangeChange={setWorkerHoursRangeKey}
        rangeOptions={workerHoursUi.rangeOptions}
        rangeAriaLabel="Worker hours date range"
        chart={
          <WorkerHoursChart
            days={workerHoursSummary.data?.days}
            range={workerHoursSummary.range}
            typeLabel={workerHoursUi.typeLabel}
            isLoading={workerHoursSummary.isLoading}
          />
        }
      />

      <ProjectSummaryCard
        title={overview.concretePours.title}
        subtitle={overview.concretePours.subtitle}
        metrics={concretePoursSummary.metrics}
        error={concretePoursSummary.error}
        typeKey={concretePoursUi.typeKey}
        onTypeChange={setConcretePoursTypeKey}
        typeOptions={concretePoursUi.typeOptions}
        typeAriaLabel="Concrete pours type"
        rangeKey={concretePoursUi.rangeKey}
        onRangeChange={setConcretePoursRangeKey}
        rangeOptions={concretePoursUi.rangeOptions}
        rangeAriaLabel="Concrete pours date range"
        chart={
          <ConcretePoursChart
            days={concretePoursSummary.data?.days}
            range={concretePoursSummary.range}
            typeLabel={concretePoursUi.typeLabel}
            isLoading={concretePoursSummary.isLoading}
          />
        }
      />

      <ProjectTicketSummaries />
    </div>
  );
};

export default ProjectOverview;
